import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAnalytics } from 'analytics';
import { ANALYTICS_EVENTS } from 'analytics/events';
import { PatientInfo } from 'widgets/home/PatientInfo';
import { formatDuration, formatReadableDate, formatReadableTime, isDateToday } from 'utils';
import { type BriefResultData, type ExportStatus, ExportStatusValue } from 'types/result';
import { ResultCardShell } from 'widgets/resultCard/ResultCardShell';

interface ResultCardProps {
  result: BriefResultData;
  resultIndex: number;
  handleStatusUpdate: (updatedStatus: ExportStatus, resultId: number) => void;
  onOpen?: () => void;
}

export const ResultCard = ({
  result,
  resultIndex,
  handleStatusUpdate,
  onOpen,
}: ResultCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const formattedDate = formatReadableDate(result.timestamp);
  const formattedTime = formatReadableTime(result.timestamp);
  const isToday = isDateToday(result.timestamp);
  const todayString = isToday ? `(${t('today')})` : '';
  const formattedDuration = `${formatDuration(result.durationSeconds)}min`;
  const patientName = result.patient?.fullname;
  const ariaPatientName = patientName || t('home.unknownPatient');

  return (
    <ResultCardShell
      resultId={result.id}
      currentStatus={result.exportStatus?.status}
      onStatusUpdate={(updatedStatus) => handleStatusUpdate(updatedStatus, result.id)}
      onClick={() => {
        if (onOpen) {
          onOpen();
          return;
        }
        trackEvent(ANALYTICS_EVENTS.HOME_OPEN_RESULT, { result_number: resultIndex + 1 });
        void navigate(`/result/${result.id}`);
      }}
      ariaLabel={`${ariaPatientName}, ${formattedDate} ${formattedTime}, Status: ${result.exportStatus?.status ?? 'loading'}`}
      infoLine={`${formattedDate} ${todayString} · ${formattedTime} · ${formattedDuration}`}
      isError={
        result.exportStatus?.status === ExportStatusValue.Failure ||
        result.exportStatus?.status === ExportStatusValue.Error
      }
      maskAnalytics
    >
      <PatientInfo
        patientName={patientName}
        birthdate={result.patient?.birthdate}
        medatixxId={result.patient?.medatixxId}
        visitReasonShort={result.visitReasonShort}
        chiefComplaint={result.chiefComplaint}
      />
    </ResultCardShell>
  );
};
